import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Receipt, DollarSign, Edit, Trash2, Filter, TrendingDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';

const CATEGORY_LABELS = {
  autoparts: 'Auto Parts', emission: 'Emission', software: 'Software',
  office_supplies: 'Office Supplies', fuel: 'Fuel', insurance: 'Insurance',
  title_processing: 'Title Processing', shipping: 'Shipping', supplies: 'Supplies',
  windshield_repair: 'Windshield Repair', auto_battery: 'Auto Battery',
  portable_equipment: 'Equipment', other: 'Other'
};

const CATEGORY_COLORS = {
  autoparts: 'bg-blue-100 text-blue-700', emission: 'bg-green-100 text-green-700',
  software: 'bg-purple-100 text-purple-700', office_supplies: 'bg-gray-100 text-gray-700',
  fuel: 'bg-amber-100 text-amber-700', insurance: 'bg-red-100 text-red-700',
  title_processing: 'bg-indigo-100 text-indigo-700', shipping: 'bg-cyan-100 text-cyan-700',
  supplies: 'bg-teal-100 text-teal-700', windshield_repair: 'bg-orange-100 text-orange-700',
  auto_battery: 'bg-yellow-100 text-yellow-700', portable_equipment: 'bg-pink-100 text-pink-700',
  other: 'bg-slate-100 text-slate-700'
};

export default function ExpenseTracker() {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [form, setForm] = useState({
    date: '', category: 'other', stock_number: '',
    amount: '', vendor: '', notes: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [expRes, sumRes] = await Promise.all([
        api.get('/api/expenses'),
        api.get('/api/expenses/summary')
      ]);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load expenses'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) || 0 };
    try {
      if (editing) {
        await api.put(`/api/expenses/${editing.id}`, payload);
        toast.success('Expense updated');
      } else {
        await api.post('/api/expenses', payload);
        toast.success('Expense added');
      }
      resetForm();
      fetchData();
    } catch { toast.error('Failed to save expense'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/api/expenses/${id}`);
      toast.success('Expense deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const startEdit = (exp) => {
    setEditing(exp);
    setForm({
      date: exp.date || '', category: exp.category || 'other',
      stock_number: exp.stock_number || '', amount: exp.amount?.toString() || '',
      vendor: exp.vendor || '', notes: exp.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setShowForm(false); setEditing(null);
    setForm({ date: '', category: 'other', stock_number: '', amount: '', vendor: '', notes: '' });
  };

  const filtered = expenses.filter(e => {
    const matchSearch = !search ||
      e.vendor?.toLowerCase().includes(search.toLowerCase()) ||
      e.stock_number?.toLowerCase().includes(search.toLowerCase()) ||
      e.notes?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCat === 'all' || e.category === filterCat;
    return matchSearch && matchCat;
  });

  if (loading) return <div className="animate-pulse space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-xl" />)}</div>;

  return (
    <div className="space-y-6" data-testid="expense-tracker">
      {/* Summary */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100" data-testid="expense-total-card">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-gray-500">Total Expenses</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">${(summary?.total_expenses || 0).toLocaleString()}</div>
        </div>
        {/* Top categories */}
        {Object.entries(summary?.by_category || {}).slice(0, 2).map(([cat, data], i) => (
          <div key={cat} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm text-gray-500">{CATEGORY_LABELS[cat] || cat}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">${data.total.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-1">{data.count} entries</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search expenses..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-xl" data-testid="expense-search" />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-44 rounded-xl" data-testid="expense-filter-cat">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl" data-testid="add-expense-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Expense
        </Button>
      </div>

      {/* Expense List */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center" data-testid="expense-empty">
          <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No expenses recorded</h3>
          <p className="text-gray-500 text-sm">Track your dealership expenses to stay on top of costs.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="expense-table">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Stock #</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Amount</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Vendor</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Notes</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(exp => (
                  <tr key={exp.id} className="hover:bg-gray-50 transition-colors" data-testid={`expense-row-${exp.id}`}>
                    <td className="px-4 py-3 text-gray-700">{exp.date || '—'}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${CATEGORY_COLORS[exp.category] || 'bg-gray-100 text-gray-700'}`}>{CATEGORY_LABELS[exp.category] || exp.category}</span></td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{exp.stock_number || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">${(exp.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-gray-600">{exp.vendor || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate">{exp.notes || '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => startEdit(exp)} className="p-1.5 rounded-lg hover:bg-blue-50 text-blue-600"><Edit className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(exp.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editing ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Date</Label>
                <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="rounded-xl" data-testid="expense-form-date" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Category *</Label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="rounded-xl" data-testid="expense-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 text-sm font-medium">Amount *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} placeholder="0.00" className="rounded-xl" required data-testid="expense-form-amount" />
              </div>
              <div>
                <Label className="text-gray-700 text-sm font-medium">Vehicle Stock #</Label>
                <Input value={form.stock_number} onChange={e => setForm({...form, stock_number: e.target.value})} placeholder="e.g. WAS0326012" className="rounded-xl" data-testid="expense-form-stock" />
              </div>
            </div>
            <div>
              <Label className="text-gray-700 text-sm font-medium">Vendor</Label>
              <Input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} placeholder="e.g. AutoZone, O'Reilly" className="rounded-xl" data-testid="expense-form-vendor" />
            </div>
            <div>
              <Label className="text-gray-700 text-sm font-medium">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Details..." className="rounded-xl" data-testid="expense-form-notes" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-red-500 to-rose-500 text-white rounded-xl" data-testid="expense-form-submit">
                {editing ? 'Update' : 'Add Expense'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="rounded-xl">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
